import { SetSchema } from "@colyseus/schema"
import Phaser, { GameObjects, Geom } from "phaser"
import type MoveTo from "phaser3-rex-plugins/plugins/moveto"
import type MoveToPlugin from "phaser3-rex-plugins/plugins/moveto-plugin"
import pkg from "../../../../../package.json"
import {
  CELL_VISUAL_HEIGHT,
  CELL_VISUAL_WIDTH,
  getRegionTint
} from "../../../../config"
import {
  FLOWER_POTS_POSITIONS_BLUE,
  FLOWER_POTS_POSITIONS_RED,
  FlowerMonByPot,
  FlowerPots
} from "../../../../core/flower-pots"
import { getPokemonData } from "../../../../models/precomputed/precomputed-pokemon-data"
import { type IPokemon, type IPokemonEntity } from "../../../../types"
import {
  AbilityAnimationArgs,
  AttackSprite,
  AttackSpriteScale
} from "../../../../types/Animation"
import { Ability } from "../../../../types/enum/Ability"
import {
  Orientation,
  PokemonActionState,
  PokemonTint,
  Stat,
  Team
} from "../../../../types/enum/Game"
import { Item } from "../../../../types/enum/Item"
import { Pkm, PkmByIndex } from "../../../../types/enum/Pokemon"
import { min } from "../../../../utils/number"
import {
  OrientationArray,
  OrientationVector
} from "../../../../utils/orientation"
import { randomBetween } from "../../../../utils/random"
import { GamePokemonDetailDOMWrapper } from "../../pages/component/game/game-pokemon-detail"
import { transformEntityCoordinates } from "../../pages/utils/utils"
import { preference } from "../../preferences"
import { DEPTH } from "../depths"
import type { DebugScene } from "../scenes/debug-scene"
import type GameScene from "../scenes/game-scene"
import { addAbilitySprite, displayAbility } from "./abilities-animations"
import DraggableObject from "./draggable-object"
import { GameDialog } from "./game-dialog"
import ItemsContainer from "./items-container"
import Lifebar from "./life-bar"
import {
  DEFAULT_POKEMON_ANIMATION_CONFIG,
  PokemonAnimations
} from "./pokemon-animations"

const spriteCountPerPokemon = new Map<string, number>()

const isGameScene = (scene: Phaser.Scene): scene is GameScene =>
  "lastPokemonDetail" in scene

export default class PokemonSprite extends DraggableObject {
  scene: GameScene | DebugScene
  evolution: Pkm
  pokemon: IPokemon | IPokemonEntity
  stages: number
  id: string
  targetX: number | null
  targetY: number | null
  positionX: number
  positionY: number
  attackSprite: AttackSprite
  itemsContainer: ItemsContainer
  orientation: Orientation
  action: PokemonActionState
  moveManager: MoveTo
  lifebar: Lifebar | undefined
  detail: GamePokemonDetailDOMWrapper | GameDialog | null = null
  sprite: GameObjects.Sprite
  shadow?: GameObjects.Sprite
  wound: GameObjects.Sprite | undefined
  burn: GameObjects.Sprite | undefined
  sleep: GameObjects.Sprite | undefined
  silence: GameObjects.Sprite | undefined
  fatigue: GameObjects.Sprite | undefined
  freeze: GameObjects.Sprite | undefined
  confusion: GameObjects.Sprite | undefined
  paralysis: GameObjects.Sprite | undefined
  pokerus: GameObjects.Sprite | undefined
  possessed: GameObjects.Sprite | undefined
  locked: GameObjects.Sprite | undefined
  blinded: GameObjects.Sprite | undefined
  armorReduction: GameObjects.Sprite | undefined
  charm: GameObjects.Sprite | undefined
  flinch: GameObjects.Sprite | undefined
  curse: GameObjects.Sprite | undefined
  poison: GameObjects.Sprite | undefined
  protect: GameObjects.Sprite | undefined
  resurrection: GameObjects.Sprite | undefined
  runeProtect: GameObjects.Sprite | undefined
  reflectShield: GameObjects.Sprite | undefined
  electricField: GameObjects.Sprite | undefined
  psychicField: GameObjects.Sprite | undefined
  grassField: GameObjects.Sprite | undefined
  fairyField: GameObjects.Sprite | undefined
  curseVulnerability: GameObjects.Sprite | undefined
  curseWeakness: GameObjects.Sprite | undefined
  curseTorment: GameObjects.Sprite | undefined
  curseFate: GameObjects.Sprite | undefined
  light: GameObjects.Sprite | undefined
  playerId: string
  shouldShowTooltip: boolean
  flip: boolean
  animationLocked: boolean /* will prevent another anim to play before current one is completed */ = false
  skydiving: boolean = false
  meal: Item | "" = ""
  mealSprite: GameObjects.Sprite | undefined
  inBattle: boolean = false
  floatingTween?: Phaser.Tweens.Tween
  troopers?: PokemonSprite[]

  constructor(
    scene: GameScene | DebugScene,
    x: number,
    y: number,
    pokemon: IPokemonEntity | IPokemon,
    playerId: string,
    inBattle: boolean,
    flip: boolean
  ) {
    super(
      scene,
      x,
      y,
      CELL_VISUAL_WIDTH,
      CELL_VISUAL_HEIGHT,
      playerId !== scene.uid
    )
    this.scene = scene
    this.flip = flip
    this.playerId = playerId
    this.shouldShowTooltip = true
    this.pokemon = pokemon
    this.stages = getPokemonData(pokemon.name).stages
    this.evolution = inBattle ? Pkm.DEFAULT : (pokemon as IPokemon).evolution
    this.width = CELL_VISUAL_WIDTH
    this.height = CELL_VISUAL_HEIGHT
    this.name = pokemon.name
    this.id = pokemon.id
    this.targetX = null
    this.targetY = null
    this.positionX = pokemon.positionX
    this.positionY = pokemon.positionY
    this.attackSprite =
      PokemonAnimations[pokemon.name]?.attackSprite ??
      DEFAULT_POKEMON_ANIMATION_CONFIG.attackSprite
    this.inBattle = inBattle
    const m = <MoveToPlugin>scene.plugins.get("rexMoveTo")
    this.moveManager = m.add(this, {
      speed: 300,
      rotateToTarget: false
    })

    if (isEntity(pokemon)) {
      this.orientation = pokemon.orientation
      this.action = pokemon.action
    } else {
      this.orientation = Orientation.DOWNLEFT
      this.action = PokemonActionState.IDLE
    }

    const textureIndex = scene.textures.exists(this.pokemon.index)
      ? this.pokemon.index
      : "0000"
    this.sprite = new GameObjects.Sprite(scene, 0, 0, "loading_pokeball")
    this.sprite.anims.play("loading_pokeball")
    const baseHP = getPokemonData(pokemon.name).hp
    const sizeBuff = (pokemon.maxHP - baseHP) / baseHP
    this.sprite
      .setScale(2 + sizeBuff)
      .setDepth(DEPTH.POKEMON)
      .setTint(getRegionTint(scene.mapName, preference("colorblindMode")))

    this.itemsContainer = new ItemsContainer(
      scene as GameScene,
      pokemon.items ?? new SetSchema(),
      this.sprite.width / 2 + 25,
      -35,
      this.id,
      playerId
    )

    const hasShadow = PokemonAnimations[pokemon.name]?.noShadow !== true
    if (hasShadow) {
      this.shadow = new GameObjects.Sprite(scene, 0, 5, textureIndex)
      this.shadow
        .setVisible(false)
        .setScale(2, 2)
        .setDepth(DEPTH.POKEMON_SHADOW)
      if (
        preference("colorblindMode") &&
        isEntity(pokemon) &&
        playerId !== scene.uid &&
        isGameScene(scene) &&
        scene.spectate === false
      ) {
        this.shadow.setTintFill(0xff0000)
      }
      this.add(this.shadow)
    }
    this.add(this.sprite)

    if (isEntity(pokemon)) {
      if (pokemon.status.light) {
        this.addLight()
      }
      if (pokemon.status.electricField) {
        this.addElectricField()
      }
      if (pokemon.status.psychicField) {
        this.addPsychicField()
      }
      if (pokemon.status.grassField) {
        this.addGrassField()
      }
      if (pokemon.status.fairyField) {
        this.addFairyField()
      }
    } else {
      if (pokemon.items.has(Item.SHINY_STONE)) {
        this.addLight()
      }
    }
    if (pokemon.items.has(Item.BERSERK_GENE)) {
      this.addBerserkEffect()
    }
    if (pokemon.items.has(Item.AIR_BALLOON)) {
      this.addFloatingAnimation()
    }
    this.add(this.itemsContainer)

    if (isEntity(pokemon)) {
      this.setLifeBar(pokemon, scene)
      //this.setEffects(p, scene);
    } else {
      if (pokemon.meal !== "") {
        this.updateMeal(pokemon.meal)
      }
    }

    this.draggable =
      playerId === scene.uid &&
      !inBattle &&
      isGameScene(scene) &&
      scene.spectate === false
    this.setDepth(DEPTH.POKEMON)

    // prevents persisting details between game transitions
    if (isGameScene(this.scene) && this.scene.lastPokemonDetail) {
      this.scene.lastPokemonDetail.closeDetail()
      this.scene.lastPokemonDetail = null
    }

    this.lazyloadAnimations(scene).then(() => {
      if (!this.sprite.scene) return
      this.sprite.setTexture(
        scene.textures.exists(this.pokemon.index) ? this.pokemon.index : "0000"
      )

      this.sprite.on(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        this.animationLocked = false
        // go back to idle anim if no more animation in queue
        scene.animationManager?.animatePokemon(this, pokemon.action, this.flip)
      })

      scene.animationManager?.animatePokemon(this, pokemon.action, this.flip)
      this.shadow?.setVisible(true)
      if (!isEntity(pokemon) && pokemon.supercharged) {
        this.superchargeAnimation(scene, true, false)
      }
      this.emit("loaded")
    })
  }

  lazyloadAnimations(scene: GameScene | DebugScene): Promise<void> {
    return new Promise((resolve) => {
      const tint = this.pokemon.shiny ? PokemonTint.SHINY : PokemonTint.NORMAL
      const pokemonSpriteKey = `${this.pokemon.index}/${tint}`
      const loadAnimations = () => {
        scene.animationManager?.createPokemonAnimations(
          this.pokemon.index,
          tint
        )
        resolve()
      }

      let spriteCount = spriteCountPerPokemon.get(pokemonSpriteKey) ?? 0
      if (spriteCount === 0 && scene?.animationManager) {
        //logger.debug("loading anims for", this.pokemon.index)
        if (scene.textures.exists(this.pokemon.index) === false) {
          // needs to load the atlas & textures first
          loadCompressedAtlas(scene, this.pokemon.index).then(loadAnimations)
        } else {
          loadAnimations()
        }
      } else {
        if (scene?.load.isLoading()) {
          scene.load.once("complete", loadAnimations)
        } else {
          loadAnimations()
        }
      }
      spriteCount++

      //logger.debug("sprite count for", this.index, spriteCount)
      spriteCountPerPokemon.set(pokemonSpriteKey, spriteCount)
    })
  }

  unloadAnimations(
    scene: GameScene | DebugScene | undefined,
    indexToUnload: string,
    tintToUnload: PokemonTint
  ) {
    const pokemonSpriteKey = `${indexToUnload}/${tintToUnload}`
    let spriteCount = spriteCountPerPokemon.get(pokemonSpriteKey) ?? 0
    spriteCount = min(0)(spriteCount - 1)
    if (spriteCount === 0 && scene?.animationManager) {
      //logger.debug("unloading anims for", indexToUnload, tintToUnload)
      scene.animationManager?.unloadPokemonAnimations(
        indexToUnload,
        tintToUnload
      )
    }
    spriteCountPerPokemon.set(pokemonSpriteKey, spriteCount)
  }

  updateTooltipPosition() {
    if (this.detail) {
      const pkmCenter = this.sprite.getCenter(undefined, true)
      const boundsScene = this.scene.cameras.main.worldView

      let x = +60
      let y = -175
      const tooltipWidth = this.detail.dom.clientWidth
      const tooltipHeight = this.detail.dom.clientHeight
      const showDetailsOnHover = preference("showDetailsOnHover")

      if (this.input && showDetailsOnHover) {
        x += this.input.localX
        y += this.input.localY
      }
      if (pkmCenter.x + x + tooltipWidth > boundsScene.right) {
        // show to the left instead
        x = -80 - tooltipWidth
        if (this.input && showDetailsOnHover) {
          x += this.input.localX
        }
      }

      const tooltipBottom = pkmCenter.y + y + tooltipHeight

      if (pkmCenter.y + y < boundsScene.top) {
        // exceeds the top edge of the camera, adjusts
        y = boundsScene.top - pkmCenter.y + 10
      } else if (tooltipBottom > boundsScene.bottom) {
        // exceeds the bottom edge of the camera, adjusts
        y -= tooltipBottom - boundsScene.bottom + 10
      }
      this.detail.setPosition(x, y)
    }
  }

  destroy(fromScene?: boolean | undefined): void {
    const g = <GameScene>this.scene
    super.destroy(fromScene)
    this.closeDetail()
    this.unloadAnimations(
      g,
      this.pokemon.index,
      this.pokemon.shiny ? PokemonTint.SHINY : PokemonTint.NORMAL
    )
  }

  closeDetail() {
    if (this.detail) {
      this.detail.dom.remove()
      this.remove(this.detail, true)
      this.detail = null
    }
  }

  openDetail() {
    const s = <GameScene>this.scene
    s.closeTooltips()
    if (s.lastPokemonDetail && s.lastPokemonDetail !== this) {
      s.lastPokemonDetail = null
    }

    this.detail = new GamePokemonDetailDOMWrapper(
      this.scene,
      0,
      0,
      this.pokemon,
      this.inBattle ? "battle" : "team",
      this.playerId === s.uid
    )
    this.detail.setDepth(DEPTH.TOOLTIP).setOrigin(0, 0)
    this.updateTooltipPosition()
    this.detail.removeInteractive()
    this.add(this.detail)
    s.lastPokemonDetail = this
  }

  onPointerDown(
    pointer: Phaser.Input.Pointer,
    event: Phaser.Types.Input.EventData
  ) {
    super.onPointerDown(pointer, event)
    if (
      this.shouldShowTooltip &&
      !preference("showDetailsOnHover") &&
      pointer.rightButtonDown() &&
      this.scene &&
      !this.detail
    ) {
      this.openDetail()
    } else {
      this.closeDetail()
    }
    if (pointer.leftButtonDown() && !this.inBattle) {
      this.emoteAnimation()
    }
  }

  onPointerUp(): void {
    super.onPointerUp()
    if (
      this.shouldShowTooltip &&
      preference("showDetailsOnHover") &&
      !this.detail
    ) {
      this.openDetail()
    }
  }

  onPointerOut(): void {
    super.onPointerOut()
    if (this.shouldShowTooltip && preference("showDetailsOnHover")) {
      this.closeDetail()
    }
  }

  onPointerOver(pointer) {
    super.onPointerOver(pointer)

    if (
      preference("showDetailsOnHover") &&
      this.shouldShowTooltip &&
      this.detail == null &&
      !pointer.leftButtonDown() // we're dragging another pokemon
    ) {
      this.openDetail()
    }
  }

  attackAnimation(
    targetX: number,
    targetY: number,
    delayBeforeShoot: number,
    travelTime: number,
    onComplete?: () => void
  ) {
    const isRange = this.attackSprite.endsWith("/range")
    const startX = isRange ? this.positionX : targetX
    const startY = isRange ? this.positionY : targetY
    const LATENCY_COMPENSATION = 20
    let attackSprite = this.attackSprite
    let tint = 0xffffff

    if (startX != null && startY != null) {
      const coordinates = transformEntityCoordinates(startX, startY, this.flip)
      let scale = AttackSpriteScale[attackSprite]

      if (attackSprite === AttackSprite.DRAGON_GREEN_RANGE) {
        attackSprite = AttackSprite.DRAGON_RANGE
        scale = [1.5, 1.5]
        tint = 0x80ff60
      }

      const projectile = this.scene.add.sprite(
        coordinates[0],
        coordinates[1] - 10,
        "attacks",
        `${attackSprite}/000.png`
      )

      projectile
        .setScale(scale[0], scale[1])
        .setTint(tint)
        .setDepth(DEPTH.PROJECTILE)
        .setVisible(false)

      if (!isRange) {
        projectile.anims.play({
          key: attackSprite,
          showOnStart: true,
          delay: delayBeforeShoot - LATENCY_COMPENSATION
        })
        projectile.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () =>
          projectile.destroy()
        )
        onComplete?.()
      } else {
        projectile.anims.play({ key: attackSprite })
        const coordinatesTarget = transformEntityCoordinates(
          targetX,
          targetY,
          this.flip
        )

        /*logger.debug(
          `Shooting a projectile to (${this.targetX},${this.targetY}) travel time ${travelTime}ms delay ${delayBeforeShoot}ms`
        )*/
        this.scene.tweens.add({
          targets: projectile,
          x: coordinatesTarget[0] + randomBetween(-5, 5),
          y: coordinatesTarget[1] + randomBetween(-5, 5),
          ease: "Linear",
          duration: min(250)(travelTime),
          delay: delayBeforeShoot - LATENCY_COMPENSATION,
          onComplete: () => {
            projectile.destroy()
            onComplete?.()
          },
          onStop: () => projectile.destroy(),
          onStart: () => projectile.setVisible(true)
        })
      }
    }
  }

  deathAnimation() {
    if (this.lifebar) {
      this.lifebar.setHp(0)
    }

    this.scene.add.tween({
      targets: [this],
      ease: "Linear",
      duration: 1500,
      delay: 0,
      alpha: {
        getStart: () => 1,
        getEnd: () => 0
      },
      onComplete: () => {
        this.destroy()
      }
    })

    if (this.troopers) {
      this.troopers.forEach((trooper, i) => {
        // all troopers run away when leader dies
        trooper.addFlinch()
        trooper.orientation = OrientationArray[(i + 6) % 8]
        const [dx, dy] = OrientationVector[(i + 5) % 8]
        const endX = trooper.x + 1000 * dx
        const endY = trooper.y + 1000 * dy

        this.scene.tweens.add({
          targets: trooper,
          x: endX,
          y: endY,
          ease: "Linear",
          duration: 5000,
          delay: 500,
          onStart: () => {
            trooper.animationLocked = false
            this.scene.animationManager?.animatePokemon(
              trooper,
              PokemonActionState.WALK,
              false
            )
          },
          onComplete: () => {
            trooper.destroy()
          }
        })
      })
    }
  }

  resurrectAnimation() {
    if (this.lifebar) {
      this.lifebar.setHp(0)
    }

    const resurrectAnim = this.scene.add.sprite(0, -10, "RESURRECT", "000")
    resurrectAnim.setDepth(DEPTH.BOOST_FRONT)
    resurrectAnim.setScale(2, 2)
    resurrectAnim.anims.play("RESURRECT")
    resurrectAnim.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      resurrectAnim.destroy()
    })

    this.add(resurrectAnim)
  }

  displayAnimation(anim: string, args: Partial<AbilityAnimationArgs> = {}) {
    return displayAbility({
      scene: this.scene as GameScene,
      pokemonsOnBoard: [],
      ability: anim,
      orientation: this.orientation,
      positionX: this.positionX,
      positionY: !this.inBattle
        ? this.positionY - 1
        : "team" in this.pokemon && this.pokemon.team === Team.RED_TEAM
          ? 4 - this.positionY
          : this.positionY,
      targetX: this.targetX ?? -1,
      targetY: this.targetY ?? -1,
      flip: this.flip,
      ap: this.pokemon.ap,
      ...args
    })
  }

  fishingAnimation() {
    this.displayAnimation("FISHING")
    const g = <GameScene>this.scene
    g.animationManager?.animatePokemon(this, PokemonActionState.HOP, this.flip)
    this.sprite.once(Phaser.Animations.Events.ANIMATION_REPEAT, () => {
      const g = <GameScene>this.scene
      g.animationManager?.animatePokemon(
        this,
        PokemonActionState.IDLE,
        this.flip
      )
    })
  }

  blossomAnimation() {
    const scene = <GameScene>this.scene
    const flowerPot = FlowerPots.find((pot) =>
      FlowerMonByPot[pot].includes(PkmByIndex[this.pokemon.index])
    )
    if (flowerPot) {
      scene.board?.flowerPokemonsInPots
        .find((p) => p.pokemon.index === this.pokemon.index)
        ?.destroy()
      const positions =
        "team" in this.pokemon && this.pokemon.team === Team.RED_TEAM
          ? this.flip
            ? FLOWER_POTS_POSITIONS_BLUE
            : FLOWER_POTS_POSITIONS_RED
          : this.flip
            ? FLOWER_POTS_POSITIONS_RED
            : FLOWER_POTS_POSITIONS_BLUE
      const [startX, startY] = positions[FlowerPots.indexOf(flowerPot)]
      addAbilitySprite(scene, Ability.PETAL_BLIZZARD, 0, [startX, startY - 24])
      this.moveManager.setEnable(false)
      this.setPosition(startX, startY)
      const [x, y] = transformEntityCoordinates(
        this.positionX,
        this.positionY,
        this.flip
      )

      scene.animationManager?.animatePokemon(
        this,
        PokemonActionState.HOP,
        this.flip,
        false
      )
      scene.tweens.add({
        targets: this,
        x,
        y,
        duration: 1000,
        onComplete: () => {
          this.moveManager.setEnable(true)
        }
      })
    }
  }

  emoteAnimation() {
    const g = <GameScene>this.scene
    g.animationManager?.animatePokemon(
      this,
      PokemonActionState.EMOTE,
      this.flip,
      false
    )
  }

  evolutionAnimation() {
    this.displayAnimation("EVOLUTION")
    this.emoteAnimation()
  }

  spawnAnimation() {
    this.displayAnimation("SPAWN")
    this.emoteAnimation()
  }

  hatchAnimation() {
    this.displayAnimation("HATCH")
    const g = <GameScene>this.scene
    g.animationManager?.animatePokemon(
      this,
      PokemonActionState.EMOTE,
      this.flip,
      false
    )
  }

  cookAnimation(dishes: Item[]) {
    this.emoteAnimation()
    dishes.forEach((item, i) => {
      const shinyEffect = this.scene.add.sprite(this.x, this.y, "shine")
      shinyEffect.setScale(2).setDepth(DEPTH.ITEM_FOUND)
      shinyEffect.play("shine")
      const itemSprite = this.scene.add.sprite(
        this.x,
        this.y,
        "item",
        item + ".png"
      )
      itemSprite.setScale(0.5).setDepth(DEPTH.ITEM_FOUND)
      this.scene.tweens.add({
        targets: [itemSprite, shinyEffect],
        ease: Phaser.Math.Easing.Quadratic.Out,
        duration: 1000,
        y: this.y - 70,
        x: this.x + (i - (dishes.length - 1) / 2) * 70,
        onComplete: () => {
          setTimeout(() => {
            itemSprite.destroy()
            shinyEffect.destroy()
          }, 1000)
        }
      })
    })
  }

  digAnimation(buriedItem: Item | null) {
    this.orientation = Orientation.UP
    const g = <GameScene>this.scene
    g.animationManager?.animatePokemon(
      this,
      PokemonActionState.WALK,
      false,
      true
    )
    this.displayAnimation("DIG")
    setTimeout(() => {
      this.orientation = Orientation.DOWNLEFT
      this.animationLocked = false
      g.animationManager?.animatePokemon(this, PokemonActionState.IDLE, false)
      if (buriedItem) {
        this.emoteAnimation()
        const shinyEffect = this.scene.add.sprite(this.x, this.y, "shine")
        shinyEffect.setScale(2).setDepth(DEPTH.ITEM_FOUND)
        shinyEffect.play("shine")
        const itemSprite = this.scene.add.sprite(
          this.x,
          this.y,
          "item",
          buriedItem + ".png"
        )
        itemSprite.setScale(0.5).setDepth(DEPTH.ITEM_FOUND)

        this.scene.tweens.add({
          targets: [itemSprite, shinyEffect],
          ease: Phaser.Math.Easing.Quadratic.Out,
          duration: 1000,
          y: this.y - 70,
          x: this.x,
          onComplete: () => {
            setTimeout(() => {
              itemSprite.destroy()
              shinyEffect.destroy()
              if (buriedItem === Item.COIN) {
                g.displayMoneyGain(this.x, this.y - 70, 1)
              } else if (buriedItem === Item.NUGGET) {
                g.displayMoneyGain(this.x, this.y - 70, 3)
              } else if (buriedItem === Item.BIG_NUGGET) {
                g.displayMoneyGain(this.x, this.y - 70, 10)
              }
            }, 1000)
          }
        })
      }
    }, 1000)
  }

  superchargeAnimation(
    scene: GameScene | DebugScene,
    alreadyActive: boolean,
    onEntity: boolean
  ) {
    this.addElectricField()
    this.sprite.postFX.addGlow(0xffff00, 4, 0, false, 0.1, 8)
    this.emoteAnimation()
    if (!alreadyActive) {
      if (!preference("disableCameraShake")) scene.cameras.main.flash(250)
      this.displayAnimation(Ability.THUNDER_SHOCK, {
        targetX: this.positionX,
        targetY: onEntity ? this.positionY : this.positionY - 1
      })
    }
  }

  updateMeal(meal: Item | "") {
    this.meal = meal
    this.mealSprite?.destroy()
    if (meal) {
      this.mealSprite = this.scene.add
        .sprite(0, 20, "item", meal + ".png")
        .setScale(0.25)
      this.add(this.mealSprite)
    }
  }

  specialAttackAnimation(pokemon: IPokemonEntity) {
    let anim =
      PokemonAnimations[PkmByIndex[pokemon.index]].ability ??
      DEFAULT_POKEMON_ANIMATION_CONFIG.ability
    if (pokemon.skill === Ability.LASER_BLADE && pokemon.count.ult % 2 === 0) {
      anim =
        PokemonAnimations[PkmByIndex[pokemon.index]].emote ??
        DEFAULT_POKEMON_ANIMATION_CONFIG.emote
    }
    if (pokemon.skill === Ability.GROWTH) {
      this.sprite.setScale(2 + 0.5 * pokemon.count.ult)
    }

    this.scene.animationManager?.play(this, anim, {
      flip: this.flip,
      lock: true,
      repeat: 0
    })
  }

  setLifeBar(pokemon: IPokemonEntity, scene: GameScene | DebugScene) {
    if (pokemon.hp !== undefined) {
      this.lifebar = new Lifebar(
        scene,
        0,
        25,
        pokemon.hp,
        pokemon.hp,
        pokemon.shield,
        pokemon.team as Team,
        this.flip
      )
      this.lifebar.setShield(pokemon.shield)
      this.add(this.lifebar)

      if (pokemon.pp !== undefined && pokemon.maxPP > 0)
        this.lifebar.setMaxPP(pokemon.maxPP)
    }
  }

  addWound() {
    if (!this.wound) {
      this.wound = this.scene.add
        .sprite(0, -30, "status", "WOUND/000.png")
        .setScale(2)
      this.wound.anims.play("WOUND")
      this.add(this.wound)
    }
  }

  removeWound() {
    if (this.wound) {
      this.remove(this.wound, true)
      this.wound = undefined
    }
  }

  addBurn() {
    if (!this.burn) {
      this.burn = this.scene.add
        .sprite(0, -30, "status", "BURN/000.png")
        .setScale(2)
      this.burn.anims.play("BURN")
      this.add(this.burn)
    }
  }

  removeBurn() {
    if (this.burn) {
      this.remove(this.burn, true)
      this.burn = undefined
    }
  }

  addSleep() {
    if (!this.sleep) {
      this.sleep = this.scene.add
        .sprite(0, -30, "status", "SLEEP/000.png")
        .setScale(2)
      this.sleep.anims.play("SLEEP")
      this.add(this.sleep)
    }
  }

  removeSleep() {
    if (this.sleep) {
      this.remove(this.sleep, true)
      this.sleep = undefined
    }
  }

  addSilence() {
    if (!this.silence) {
      this.silence = this.scene.add
        .sprite(0, -30, "status", "SILENCE/000.png")
        .setScale(2)
      this.silence.anims.play("SILENCE")
      this.add(this.silence)
    }
  }

  removeSilence() {
    if (this.silence) {
      this.remove(this.silence, true)
      this.silence = undefined
    }
  }

  addFatigue() {
    if (!this.fatigue) {
      this.fatigue = this.scene.add
        .sprite(0, -10, "status", "FATIGUE/000.png")
        .setScale(2)
      this.fatigue.anims.play("FATIGUE")
      this.add(this.fatigue)
    }
  }

  removeFatigue() {
    if (this.fatigue) {
      this.remove(this.fatigue, true)
      this.fatigue = undefined
    }
  }

  addFreeze() {
    if (!this.freeze) {
      this.freeze = this.scene.add
        .sprite(0, 0, "status", "FREEZE/000.png")
        .setScale(2)
      this.freeze.anims.play("FREEZE")
      this.add(this.freeze)
    }
  }

  removeFreeze() {
    if (this.freeze) {
      this.remove(this.freeze, true)
      this.freeze = undefined
    }
  }

  addConfusion() {
    if (!this.confusion) {
      this.confusion = this.scene.add
        .sprite(0, -30, "status", "CONFUSION/000.png")
        .setScale(2)
      this.confusion.anims.play("CONFUSION")
      this.add(this.confusion)
    }
  }

  removeConfusion() {
    if (this.confusion) {
      this.remove(this.confusion, true)
      this.confusion = undefined
    }
  }

  addParalysis() {
    if (!this.paralysis) {
      this.paralysis = this.scene.add
        .sprite(0, -30, "status", "PARALYSIS/000.png")
        .setScale(2)
      this.paralysis.anims.play("PARALYSIS")
      this.add(this.paralysis)
    }
  }

  removeParalysis() {
    if (this.paralysis) {
      this.remove(this.paralysis, true)
      this.paralysis = undefined
    }
  }

  addPokerus() {
    if (!this.pokerus) {
      this.pokerus = this.scene.add
        .sprite(0, -50, "status", "POKERUS/000.png")
        .setScale(2)
      this.pokerus.anims.play("POKERUS")
      this.add(this.pokerus)
    }
  }

  removePokerus() {
    if (this.pokerus) {
      this.remove(this.pokerus, true)
      this.pokerus = undefined
    }
  }

  addPossessed() {
    if (!this.possessed) {
      this.possessed = this.scene.add
        .sprite(-16, -24, "status", "POSSESSED/000.png")
        .setScale(2)
      this.possessed.anims.play("POSSESSED")
      this.sprite.setTint(0xff50ff)
      this.add(this.possessed)
      //this.bringToTop(this.sprite)
    }
  }

  removePossessed() {
    if (this.possessed) {
      this.sprite.clearTint()
      this.remove(this.possessed, true)
      this.possessed = undefined
    }
  }

  addLocked() {
    if (!this.locked) {
      this.locked = this.scene.add
        .sprite(0, -30, "status", "LOCKED/000.png")
        .setScale(2)
      this.locked.anims.play("LOCKED")
      this.add(this.locked)
    }
  }

  removeLocked() {
    if (this.locked) {
      this.remove(this.locked, true)
      this.locked = undefined
    }
  }

  addBlinded() {
    if (!this.blinded) {
      this.blinded = this.scene.add
        .sprite(0, -30, "status", "BLINDED/000.png")
        .setScale(3)
      this.blinded.anims.play("BLINDED")
      this.add(this.blinded)
    }
  }

  removeBlinded() {
    if (this.blinded) {
      this.remove(this.blinded, true)
      this.blinded = undefined
    }
  }

  addArmorReduction() {
    if (!this.armorReduction) {
      this.armorReduction = this.scene.add
        .sprite(0, -40, "status", "ARMOR_BREAK/000.png")
        .setScale(2)
      this.armorReduction.anims.play("ARMOR_BREAK")
      this.add(this.armorReduction)
    }
  }

  removeArmorReduction() {
    if (this.armorReduction) {
      this.remove(this.armorReduction, true)
      this.armorReduction = undefined
    }
  }

  addCharm() {
    if (!this.charm) {
      this.charm = this.scene.add
        .sprite(0, -40, "status", "CHARM/000.png")
        .setScale(2)
      this.charm.anims.play("CHARM")
      this.add(this.charm)
    }
  }

  removeCharm() {
    if (this.charm) {
      this.remove(this.charm, true)
      this.charm = undefined
    }
  }

  addFlinch() {
    if (!this.flinch) {
      this.flinch = this.scene.add
        .sprite(0, -40, "status", "FLINCH/000.png")
        .setScale(2)
      this.flinch.anims.play("FLINCH")
      this.add(this.flinch)
    }
  }

  removeFlinch() {
    if (this.flinch) {
      this.remove(this.flinch, true)
      this.flinch = undefined
    }
  }

  addCurse() {
    if (!this.curse) {
      this.curse = this.scene.add
        .sprite(0, -65, "status", "CURSE/000.png")
        .setScale(1.5)
      this.curse.anims.play("CURSE")
      this.add(this.curse)
    }
  }

  removeCurse() {
    if (this.curse) {
      this.remove(this.curse, true)
      this.curse = undefined
    }
  }

  addCurseVulnerability() {
    if (!this.curseVulnerability) {
      this.curseVulnerability = this.scene.add
        .sprite(0, 15, "abilities", "CURSE_OF_VULNERABILITY/000.png")
        .setScale(1)
      this.curseVulnerability.anims.play("CURSE_OF_VULNERABILITY")
      this.add(this.curseVulnerability)
    }
  }

  addCurseWeakness() {
    if (!this.curseWeakness) {
      this.curseWeakness = this.scene.add
        .sprite(-30, -15, "abilities", "CURSE_OF_WEAKNESS/000.png")
        .setScale(1)
      this.curseWeakness.anims.play("CURSE_OF_WEAKNESS")
      this.add(this.curseWeakness)
    }
  }

  addCurseTorment() {
    if (!this.curseTorment) {
      this.curseTorment = this.scene.add
        .sprite(30, -15, "abilities", "CURSE_OF_TORMENT/000.png")
        .setScale(1)
      this.curseTorment.anims.play("CURSE_OF_TORMENT")
      this.add(this.curseTorment)
    }
  }

  addCurseFate() {
    if (!this.curseFate) {
      this.curseFate = this.scene.add
        .sprite(0, -45, "abilities", "CURSE_OF_FATE/000.png")
        .setScale(1)
      this.curseFate.anims.play("CURSE_OF_FATE")
      this.add(this.curseFate)
    }
  }

  addPoison() {
    if (!this.poison) {
      this.poison = this.scene.add
        .sprite(0, -30, "status", "POISON/000.png")
        .setScale(2)
      this.poison.anims.play("POISON")
      this.add(this.poison)
    }
  }

  removePoison() {
    if (this.poison) {
      this.remove(this.poison, true)
      this.poison = undefined
    }
  }

  addProtect() {
    if (!this.protect) {
      this.protect = this.scene.add
        .sprite(0, -30, "status", "PROTECT/000.png")
        .setScale(2)
      this.protect.anims.play("PROTECT")
      this.add(this.protect)
    }
  }

  removeProtect() {
    if (this.protect) {
      this.remove(this.protect, true)
      this.protect = undefined
    }
  }

  skydiveUp() {
    if (!this.skydiving) {
      // animation where pokemon is flying up out of the screen for a screen dive animation. Should take <= 500 milliseconds
      this.skydiving = true
      this.moveManager.setSpeed(1000)
      this.moveManager.moveTo(this.x, -100)
    }
  }

  skydiveDown() {
    if (this.skydiving) {
      // animation after a skydiving attack where pokemon moves from its target cell to its final reserved adjacent cell
      const landingCoordinates = transformEntityCoordinates(
        this.targetX ?? this.positionX,
        this.targetY ?? this.positionY,
        this.flip
      )
      const finalCoordinates = transformEntityCoordinates(
        this.positionX,
        this.positionY,
        this.flip
      )

      this.x = landingCoordinates[0]
      this.y = landingCoordinates[1]
      this.moveManager.setSpeed(1000)
      this.moveManager.moveTo(finalCoordinates[0], finalCoordinates[1])
      this.skydiving = false
    }
  }

  addResurrection() {
    if (!this.resurrection) {
      this.resurrection = this.scene.add
        .sprite(0, -45, "status", "RESURRECTION/000.png")
        .setScale(2)
      this.resurrection.anims.play("RESURRECTION")
      this.add(this.resurrection)
    }
  }

  removeResurrection() {
    if (this.resurrection) {
      this.remove(this.resurrection, true)
      this.resurrection = undefined
    }
  }

  addRuneProtect() {
    if (!this.runeProtect) {
      this.runeProtect = this.scene.add
        .sprite(0, -40, "status", "RUNE_PROTECT/000.png")
        .setScale(2)
      this.runeProtect.anims.play("RUNE_PROTECT")
      this.add(this.runeProtect)
    }
  }

  removeRuneProtect() {
    if (this.runeProtect) {
      this.remove(this.runeProtect, true)
      this.runeProtect = undefined
    }
  }

  addReflectShieldAnim(colorVariation = 0xffffff) {
    if (!this.reflectShield) {
      this.reflectShield = this.scene.add
        .sprite(0, -5, "abilities", `${Ability.REFLECT}/000.png`)
        .setScale(2)
        .setTint(colorVariation)
      this.reflectShield.anims.play(Ability.REFLECT)
      this.add(this.reflectShield)
    }
  }

  removeReflectShieldAnim() {
    if (this.reflectShield) {
      this.remove(this.reflectShield, true)
      this.reflectShield = undefined
    }
  }

  addLight() {
    if (this.light) return
    this.light = this.scene.add
      .sprite(0, 0, "abilities", "LIGHT_CELL/000.png")
      .setScale(1.5, 1.5)
      .setDepth(DEPTH.LIGHT_CELL)
    this.light.anims.play("LIGHT_CELL")
    this.add(this.light)
    this.sendToBack(this.light)
  }

  removeLight() {
    if (this.light) {
      this.remove(this.light, true)
      this.light = undefined
    }
  }

  addElectricField() {
    if (!this.electricField) {
      this.electricField = this.scene.add
        .sprite(3, 3, "status", "ELECTRIC_FIELD/000.png")
        .setDepth(DEPTH.BOARD_EFFECT_GROUND_LEVEL)
        .setScale(1)
      this.electricField.anims.play("ELECTRIC_FIELD")
      this.add(this.electricField)
      this.bringToTop(this.sprite)
    }
  }

  removeElectricField() {
    if (this.electricField) {
      this.remove(this.electricField, true)
      this.electricField = undefined
    }
  }

  addGrassField() {
    if (!this.grassField) {
      this.grassField = this.scene.add
        .sprite(0, 10, "abilities", "GRASSY_FIELD/000.png")
        .setDepth(DEPTH.BOARD_EFFECT_GROUND_LEVEL)
        .setScale(2)
      this.scene.add.existing(this.grassField)
      this.grassField.anims.play("GRASSY_FIELD")
      this.add(this.grassField)
      this.bringToTop(this.sprite)
    }
  }

  removeGrassField() {
    if (this.grassField) {
      this.remove(this.grassField, true)
      this.grassField = undefined
    }
  }

  addFairyField() {
    if (!this.fairyField) {
      this.fairyField = this.scene.add
        .sprite(0, 10, "status", "FAIRY_FIELD/000.png")
        .setDepth(DEPTH.BOARD_EFFECT_GROUND_LEVEL)
        .setScale(1.5)
      this.fairyField.anims.play("FAIRY_FIELD")
      this.add(this.fairyField)
      this.bringToTop(this.sprite)
    }
  }

  removeFairyField() {
    if (this.fairyField) {
      this.remove(this.fairyField, true)
      this.fairyField = undefined
    }
  }

  addPsychicField() {
    if (!this.psychicField) {
      this.psychicField = this.scene.add
        .sprite(0, 10, "status", "PSYCHIC_FIELD/000.png")
        .setDepth(DEPTH.BOARD_EFFECT_GROUND_LEVEL)
        .setScale(1)
      this.psychicField.anims.play("PSYCHIC_FIELD")
      this.add(this.psychicField)
      this.bringToTop(this.sprite)
    }
  }

  removePsychicField() {
    if (this.psychicField) {
      this.remove(this.psychicField, true)
      this.psychicField = undefined
    }
  }

  addRageEffect() {
    this.sprite.setTint(0xff0000)
  }

  removeRageEffect(hasBerserkGene: boolean = false) {
    if (hasBerserkGene) {
      this.addBerserkEffect()
    } else {
      this.sprite.clearTint()
    }
  }

  addBerserkEffect() {
    this.sprite.setTint(0x00ff00)
  }

  removeBerserkEffect() {
    this.sprite.clearTint()
  }

  addFloatingAnimation() {
    this.floatingTween = this.scene.tweens.add({
      targets: this.sprite,
      y: { from: this.sprite.y - 10, to: this.sprite.y - 20 },
      duration: 500,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1
    })
  }

  removeFloatingAnimation() {
    if (this.floatingTween) {
      this.floatingTween.stop()
      this.floatingTween = undefined
    }
  }

  addFlowerTrick() {
    const flowerTrick = this.scene.add.container()

    for (let i = 0; i < 5; i++) {
      const flowerSprite = this.scene.add
        .sprite(0, 0, "abilities", `${Ability.FLOWER_TRICK}/000.png`)
        .setScale(2)
      flowerSprite.anims.play({
        key: Ability.FLOWER_TRICK,
        frameRate: 7,
        repeat: -1
      })
      flowerTrick.add(flowerSprite)
    }
    const circle = new Geom.Circle(0, 0, 48)
    Phaser.Actions.PlaceOnCircle(flowerTrick.getAll(), circle)

    this.add(flowerTrick)

    this.scene.tweens.add({
      targets: circle,
      radius: 50,
      ease: Phaser.Math.Easing.Quartic.Out,
      duration: 3000,
      onUpdate: function (tween) {
        Phaser.Actions.RotateAroundDistance(
          flowerTrick.getAll(),
          { x: 0, y: 0 },
          0.08,
          circle.radius
        )
      },
      onComplete: function () {
        flowerTrick.destroy(true)
      }
    })
  }

  displayBoost(stat: Stat, debug?: boolean) {
    const tint =
      {
        [Stat.AP]: 0xff00aa,
        [Stat.SPEED]: 0xffaa44,
        [Stat.ATK]: 0xff6633,
        [Stat.DEF]: 0xffaa66,
        [Stat.SPE_DEF]: 0xff99cc,
        [Stat.SHIELD]: 0xffcc99
      }[stat] ?? 0xffffff

    const boost = new GameObjects.Sprite(
      this.scene,
      0,
      -20,
      "abilities",
      `BOOST/000.png`
    )
      .setDepth(DEPTH.BOOST_BACK)
      .setScale(2)
      .setTint(tint)

    this.add(boost)
    boost.anims.play({
      key: "BOOST",
      repeat: debug ? 5 : 0
    })
    boost.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      boost.destroy()
    })
  }
}

export const isEntity = (
  pokemon: IPokemon | IPokemonEntity
): pokemon is IPokemonEntity => {
  return "status" in pokemon
}

const lazyLoadingRequests = {}

export function loadCompressedAtlas(
  scene: Phaser.Scene,
  index: string
): Promise<void> {
  if (index in lazyLoadingRequests) {
    return lazyLoadingRequests[index]
  }
  lazyLoadingRequests[index] = new Promise((resolve) => {
    scene.load.once(
      `filecomplete-json-pokemon-atlas-${index}`,
      (key, type, data) => {
        const image = data.i

        function traverse(obj: any, path: string, frames) {
          if (Array.isArray(obj)) {
            const [
              sourceSizew,
              sourceSizeh,
              spriteSourceSizex,
              spriteSourceSizey,
              spriteSourceSizew,
              spriteSourceSizeh,
              framex,
              framey,
              framew,
              frameh
            ] = obj
            frames.push({
              filename: path,
              rotated: false,
              trimmed: true,
              sourceSize: {
                w: sourceSizew,
                h: sourceSizeh
              },
              spriteSourceSize: {
                x: spriteSourceSizex,
                y: spriteSourceSizey,
                w: spriteSourceSizew,
                h: spriteSourceSizeh
              },
              frame: {
                x: framex,
                y: framey,
                w: framew,
                h: frameh
              }
            })
          } else if (obj instanceof Object) {
            for (const key in obj) {
              traverse(obj[key], path ? path + "/" + key : key, frames)
            }
          }
        }
        const frames = []

        traverse(data.a, "", frames)

        const multiatlas = {
          textures: [
            {
              image: `${image}?v=${pkg.version}`,
              format: "RGBA8888",
              size: {
                w: data.s[0],
                h: data.s[1]
              },
              scale: data.s[2] ?? 1,
              frames
            }
          ]
        }

        const index = image.replace(".png", "")

        //console.log("load multiatlas " + index)
        scene.textures.once(`addtexture-${index}`, () => {
          delete lazyLoadingRequests[index]
          resolve(index)
        })
        // @ts-ignore: there is an error in phaser types, the second parameter can be an object
        scene.load.multiatlas(index, multiatlas, "/assets/pokemons").start()
      }
    )
    scene.load
      .json(
        `pokemon-atlas-${index}`,
        `/assets/pokemons/${index}.json?v=${pkg.version}`
      )
      .start()
  })
  return lazyLoadingRequests[index]
}
